import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { workspaceTeamAPI } from '../services/api';

const TeamInvitations = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [invRes, wsRes] = await Promise.all([
        workspaceTeamAPI.getMyInvitations(),
        workspaceTeamAPI.getMyWorkspaces()
      ]);
      setInvitations(invRes.data.invitations || []);
      setWorkspaces(wsRes.data.workspaces || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAccept = async (ownerId) => {
    setActionLoading(ownerId);
    try {
      const res = await workspaceTeamAPI.acceptInvitation({ owner_id: ownerId });
      setSuccess(res.data.message || 'Invitation acceptée !');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDecline = async (ownerId) => {
    if (!window.confirm('Voulez-vous vraiment refuser cette invitation ?')) return;
    setActionLoading(ownerId);
    try {
      await workspaceTeamAPI.declineInvitation({ owner_id: ownerId });
      setSuccess('Invitation refusée.');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleLeave = async (ownerId, ownerName) => {
    if (!window.confirm(`Voulez-vous vraiment quitter l'équipe de ${ownerName} ?`)) return;
    setActionLoading(ownerId);
    try {
      await workspaceTeamAPI.leaveWorkspace(ownerId);
      setSuccess(`Vous avez quitté l'équipe de ${ownerName}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">🏢 Workspaces partagés</h1>
        <p className="text-gray-500 mt-1">
          Gérez les invitations reçues et accédez aux workspaces de votre équipe
        </p>
      </div>

      {/* Alertes */}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl flex items-center gap-2">
          ✅ {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-400 hover:text-green-600">✕</button>
        </div>
      )}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl flex items-center gap-2">
          ⚠️ {error}
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Invitations en attente */}
      {invitations.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="bg-yellow-100 text-yellow-600 rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">
              {invitations.length}
            </span>
            Invitations en attente
          </h2>

          <div className="space-y-3">
            {invitations.map(inv => (
              <div key={inv.id} className="bg-white rounded-2xl shadow-sm border-2 border-yellow-200 p-5 hover:shadow-md transition">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Propriétaire */}
                  <div className="flex items-center gap-3 flex-1">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                      {inv.owner_photo ? (
                        <img src={inv.owner_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        inv.owner_name?.charAt(0)?.toUpperCase() || '?'
                      )}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{inv.owner_name}</div>
                      <div className="text-sm text-gray-500">{inv.owner_email}</div>
                    </div>
                  </div>

                  {/* Rôle proposé */}
                  <div className="flex items-center gap-2 bg-purple-50 px-3 py-2 rounded-xl">
                    <span className="text-xl">{inv.role_info?.icon || '⚙️'}</span>
                    <div>
                      <div className="text-xs text-purple-600">Rôle proposé</div>
                      <div className="font-medium text-purple-800 text-sm">{inv.role_label}</div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDecline(inv.owner_id)}
                      disabled={actionLoading === inv.owner_id}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition text-sm disabled:opacity-50"
                    >
                      Refuser
                    </button>
                    <button
                      onClick={() => handleAccept(inv.owner_id)}
                      disabled={actionLoading === inv.owner_id}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm disabled:opacity-50 flex items-center gap-1"
                    >
                      {actionLoading === inv.owner_id ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      ) : (
                        <>✅ Accepter</>
                      )}
                    </button>
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-400">
                  Invité le {new Date(inv.invited_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Workspaces actifs */}
      <div>
        <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
          <span className="bg-green-100 text-green-600 rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">
            {workspaces.length}
          </span>
          Workspaces auxquels j'ai accès
        </h2>

        {workspaces.length === 0 && invitations.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
            <span className="text-6xl mb-4 block">🏢</span>
            <h3 className="text-xl font-bold text-gray-800 mb-2">Aucun workspace partagé</h3>
            <p className="text-gray-500 mb-4">
              Vous n'avez pas encore été invité(e) à gérer le workspace d'un autre utilisateur.
            </p>
            {user?.role === 'author' && (
              <Link
                to="/workspace"
                className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition"
              >
                💼 Aller à mon workspace
              </Link>
            )}
          </div>
        ) : workspaces.length === 0 ? (
          <div className="bg-gray-50 rounded-2xl p-6 text-center text-gray-500">
            Aucun workspace actif. Acceptez une invitation ci-dessus pour commencer.
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {workspaces.map(ws => (
              <div
                key={ws.id}
                className="bg-white rounded-2xl shadow-sm border p-5 hover:shadow-lg transition cursor-pointer group"
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {ws.owner_photo ? (
                      <img src={ws.owner_photo} alt="" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      ws.owner_name?.charAt(0)?.toUpperCase() || '?'
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-gray-900 truncate">{ws.owner_name}</div>
                    <div className="text-sm text-gray-500 truncate">{ws.owner_email}</div>
                  </div>
                </div>

                {/* Rôle */}
                <div className="bg-indigo-50 rounded-xl p-3 mb-4 flex items-center gap-2">
                  <span className="text-xl">{ws.role_info?.icon || '⚙️'}</span>
                  <div>
                    <div className="text-xs text-indigo-600 font-medium">Mon rôle</div>
                    <div className="font-semibold text-indigo-800">{ws.role_label}</div>
                  </div>
                  <div className="ml-auto text-xs text-gray-400">
                    {ws.permissions?.length || 0} permissions
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(`/guest-workspace/${ws.owner_id}`)}
                    className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm font-medium flex items-center justify-center gap-1"
                  >
                    <span>🚀</span> Accéder
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleLeave(ws.owner_id, ws.owner_name); }}
                    disabled={actionLoading === ws.owner_id}
                    className="px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition text-sm disabled:opacity-50"
                    title="Quitter cette équipe"
                  >
                    👋
                  </button>
                </div>

                {/* Dernier accès */}
                {ws.last_access && (
                  <div className="mt-3 text-xs text-gray-400 text-center">
                    Dernier accès : {new Date(ws.last_access).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamInvitations;
